/**
 * Turno que não termina do jeito normal — peças puras (sem DOM, sem Office.js).
 *
 * Um turno pode acabar de cinco jeitos além do sucesso: esgotou os passos,
 * travou repetindo a mesma ação, o usuário parou, deu erro, ou o Word fechou no
 * meio. Em todos, o documento pode ter ficado com PARTE das alterações — e
 * antes o painel jogava fora o que o turno tinha feito: o histórico do modelo
 * só era gravado no sucesso, então um "pode continuar" seguinte não sabia o que
 * já estava no documento, e a mensagem de parada não dizia o que tinha mudado.
 *
 * O que mora aqui (testado em scripts/check-session.mjs):
 *  - o REGISTRO das escritas do turno (`WriteRecord`), montado com o próprio
 *    retorno das ferramentas — que já diz o que foi feito e onde;
 *  - o AVISO de fim de passos, que o modelo recebe a poucos passos do teto para
 *    fechar num ponto consistente em vez de ser cortado no meio;
 *  - a instrução do passo de FECHAMENTO (sem ferramentas);
 *  - o histórico SINTÉTICO de um turno interrompido: pedido + nota do que
 *    aconteceu, sem blocos de ferramenta — válido para a API e pequeno.
 */
/** A quantos passos do teto o modelo é avisado para fechar (teto padrão). */
export const WRAP_UP_STEPS = 3;
/**
 * Em que ponto avisar, proporcional ao teto: 3 passos antes num teto de 40–50,
 * menos em tetos pequenos. Com teto 5, avisar a 3 do fim era avisar no 2º passo
 * — e o modelo, visto no Word, encerrava ali, com 3 passos sobrando.
 */
export function wrapUpThreshold(maxSteps) {
    return Math.max(1, Math.min(WRAP_UP_STEPS, Math.floor(maxSteps / 5)));
}
/** Teto de uma linha de resumo (o registro vai para o histórico do modelo). */
const SUMMARY_MAX_CHARS = 160;
/** Teto de escritas listadas na nota (o resto vira "+N"). */
const NOTE_MAX_WRITES = 25;
/** Primeira linha útil do retorno da ferramenta, curta. */
export function recordFromToolResult(tool, content) {
    var _a;
    const texto = typeof content === 'string' ? content : JSON.stringify(content !== null && content !== void 0 ? content : '');
    const linha = (_a = texto.split(/\r?\n/).find((l) => l.trim())) !== null && _a !== void 0 ? _a : '';
    const curta = linha.trim().replace(/\s+/g, ' ');
    return {
        tool,
        summary: curta.length > SUMMARY_MAX_CHARS ? `${curta.slice(0, SUMMARY_MAX_CHARS)}…` : curta,
    };
}
/**
 * Aviso que entra JUNTO dos resultados de ferramenta quando faltam poucos
 * passos. Vai como bloco de texto depois dos `tool_result` (a API aceita) e
 * fica no histórico — nada é apagado depois.
 */
export function wrapUpReminder(remaining) {
    const passos = remaining === 1 ? 'Resta 1 passo' : `Restam ${remaining} passos`;
    return (`[Aviso do painel] ${passos} neste pedido. Use o que resta para avançar sem deixar nada ` +
        'pela metade: não comece o que não couber nele. Se não der para concluir tudo, termine ' +
        'dizendo o que já fez e o que falta. Na resposta, não fale de passos nem de limites.');
}
/** Instrução do passo de fechamento, em que o modelo não pode usar ferramentas. */
export const FINALIZE_INSTRUCTION = '[Aviso do painel] Este pedido chegou ao limite de passos e parou aqui. Não chame ' +
    'ferramentas. Diga ao usuário, em até 4 linhas, o que você já alterou no documento neste ' +
    'pedido e o que ficou faltando — seja concreto (seções, tabelas, tópicos). Se alguma ' +
    'alteração ficou pela metade, diga qual. Não fale de passos nem de limites. Termine ' +
    'perguntando se ele quer que você continue.';
/** Mensagem `user` de resultados + um bloco de texto no fim. */
export function appendTextToToolResults(message, text) {
    const blocos = Array.isArray(message.content) ? [...message.content] : [];
    blocos.push({ type: 'text', text });
    return Object.assign(Object.assign({}, message), { content: blocos });
}
const MOTIVO = {
    max_steps: 'O pedido chegou ao limite de passos antes de terminar.',
    loop_guard: 'O pedido parou porque a mesma ação falhou repetidas vezes.',
    cancelled: 'O usuário interrompeu o pedido antes de terminar.',
    error: 'O pedido parou por um erro antes de terminar.',
};
/**
 * Nota do assistente para o histórico de um turno interrompido. É o que o
 * modelo lê no turno seguinte: o que mudou no documento e o que fazer se o
 * usuário pedir para continuar.
 */
export function interruptedTurnNote(reason, writes, detail = '') {
    const partes = [`(${MOTIVO[reason]}${detail ? ` Detalhe: ${detail.slice(0, 200)}` : ''}`];
    if (writes.length) {
        const listadas = writes
            .slice(0, NOTE_MAX_WRITES)
            .map((w, i) => `${i + 1}. ${w.tool} — ${w.summary}`);
        const resto = writes.length - listadas.length;
        partes.push(`Alterações que JÁ estão no documento:\n${listadas.join('\n')}` +
            (resto > 0 ? `\n…e mais ${resto}.` : ''));
        partes.push('Se o usuário pedir para continuar, releia a região afetada e siga do ponto em que ' +
            'parou, sem refazer o que já está feito.)');
    }
    else {
        partes.push('Nada foi alterado no documento.)');
    }
    return partes.join('\n');
}
/**
 * Histórico depois de um turno interrompido: o pedido e a nota, sem os blocos
 * de ferramenta do turno. Termina em `assistant` (a API exige alternância) e
 * não carrega `tool_use` sem `tool_result`, então o próximo envio é válido.
 */
export function appendInterruptedTurn(history, turnText, note) {
    return [
        ...history,
        { role: 'user', content: turnText },
        { role: 'assistant', content: [{ type: 'text', text: note }] },
    ];
}
